import { Suspense, lazy, useEffect, useState } from 'react'
import { useCenterOrder } from './shared/locations/order'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import Sidebar, { firstModulePath } from './shared/components/Sidebar'
import { useAuth } from './shared/auth/AuthContext'
import Topbar from './shared/components/Topbar'
import AuthGate from './shared/components/AuthGate'
import AuthCallback from './shared/components/AuthCallback'
import ErrorBoundary from './shared/components/ErrorBoundary'
import { retryImport } from './shared/reload'

// Each module is its own chunk — the shell only pays for the module the
// staff member actually opens.
const EnrollmentRoutes = lazy(retryImport(() => import('./modules/enrollment/routes')))
const StudentsRoutes = lazy(retryImport(() => import('./modules/students/routes')))
const StaffRoutes = lazy(retryImport(() => import('./modules/staff/routes')))
const MaintenanceRoutes = lazy(retryImport(() => import('./modules/maintenance/routes')))
const BillingRoutes = lazy(retryImport(() => import('./modules/billing/routes')))
const MarketingRoutes = lazy(retryImport(() => import('./modules/marketing/routes')))
const SalesRoutes = lazy(retryImport(() => import('./modules/sales/routes')))
const LoyaltyRoutes = lazy(retryImport(() => import('./modules/loyalty/routes')))
const RecruitmentRoutes = lazy(retryImport(() => import('./modules/recruitment/routes')))
const AssistantRoutes = lazy(retryImport(() => import('./modules/assistant/routes')))
const SettingsRoutes = lazy(retryImport(() => import('./modules/settings/routes')))
const InventoryRoutes = lazy(retryImport(() => import('./modules/inventory/routes')))
// Public: teachers upload inventory photos with a link, no login (doc 70)
const InventoryUpload = lazy(retryImport(() => import('./modules/inventory/PublicUpload')))

export default function App() {
  return (
    <Routes>
      {/* Kept OUTSIDE AuthGate and at this exact path — it's what's
          registered as the Google OAuth redirect URI. Moving it would mean
          re-registering a new URI in Google Cloud Console. */}
      <Route path="/enrollment/auth/callback" element={<AuthCallback />} />

      {/* Inventory photo upload for teachers (no portal access) — the token in the link is the key (doc 70) */}
      <Route
        path="/inventory/upload/:token"
        element={
          <Suspense fallback={<div style={{ padding: 24 }}>Loading…</div>}>
            <InventoryUpload />
          </Suspense>
        }
      />

      {/* Everything else requires a session — login is platform-wide, not
          per-module. A location_director signing in once gets access to
          Enrollment, Students, Staff, Maintenance alike (scoped by role). */}
      <Route
        path="/*"
        element={
          <AuthGate>
            <Shell />
          </AuthGate>
        }
      />
    </Routes>
  )
}

function Shell() {
  const { user, endPreview } = useAuth()
  const section = useLocation().pathname.split('/')[1] ?? '' // a failed screen clears when you open another section
  // Centre short names + order (Settings → Locations). Subscribing here re-renders every screen when they
  // change; on the very first visit (nothing cached) screens wait for them so names never flicker.
  const order = useCenterOrder()
  const [gaveUp, setGaveUp] = useState(false)
  useEffect(() => {
    const t = setTimeout(() => setGaveUp(true), 4000) // settings API down → show the screens anyway
    return () => clearTimeout(t)
  }, [])
  const namesWait = !order.ready && !gaveUp
  const home = firstModulePath(user)
  return (
    <div style={{ display: 'flex', height: '100%' }}>
      <Sidebar />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {user?.previewBy && (
          // Preview as (Settings → Access, 2026-10-18): who you are looking as, and the way out
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 24px', background: 'var(--gold-100)',
            borderBottom: '1px solid var(--gold-500)', color: 'var(--navy-900)', fontSize: 14, flexWrap: 'wrap' }}>
            <span>
              👁 Previewing as <b>{user.name}</b> ({user.email}){user.centers ? ` · ${user.centers.length} center${user.centers.length === 1 ? '' : 's'}` : ' · all centers'}.
              Read-only — nothing is saved. Ends by itself after 30 minutes.
            </span>
            <button onClick={endPreview} style={{ marginLeft: 'auto', padding: '5px 12px', borderRadius: 8, border: '1px solid var(--navy-500)',
              background: 'var(--surface)', color: 'var(--navy-700)', fontWeight: 600, cursor: 'pointer' }}>
              Exit preview
            </button>
          </div>
        )}
        <Topbar />
        <main style={{ flex: 1, padding: '20px 24px', overflowY: 'auto' }}>
          <ErrorBoundary key={section}>
          <Suspense fallback={<div style={{ color: 'var(--text-muted)' }}>Loading…</div>}>
            {namesWait ? <div style={{ color: 'var(--text-muted)' }}>Loading…</div> : <Routes>
              <Route path="/" element={<Navigate to={home} replace />} />
              <Route path="/enrollment/*" element={<EnrollmentRoutes />} />
              <Route path="/students/*" element={<StudentsRoutes />} />

              {/* Next modules — same lazy-route pattern once built. */}
              <Route path="/staff/*" element={<StaffRoutes />} />
              <Route path="/maintenance/*" element={<MaintenanceRoutes />} />
              <Route path="/inventory/*" element={<InventoryRoutes />} />
              <Route path="/billing/*" element={<BillingRoutes />} />
              <Route path="/marketing/*" element={<MarketingRoutes />} />
              <Route path="/sales/*" element={<SalesRoutes />} />
              <Route path="/loyalty/*" element={<LoyaltyRoutes />} />
              <Route path="/recruitment/*" element={<RecruitmentRoutes />} />
              <Route path="/assistant/*" element={<AssistantRoutes />} />
              <Route path="/settings/*" element={<SettingsRoutes />} />

              <Route path="*" element={<Navigate to={home} replace />} />
            </Routes>}
          </Suspense>
          </ErrorBoundary>
        </main>
      </div>
    </div>
  )
}
